import { SessionType } from '@sondermind/utilities/models-session';
import { AvailabilityResponse, IAvailabilityApiResponse } from './availability-response';
import { SchedulingServiceSessionType } from './scheduling-service-session-type.enum';
import { TimeSlot } from './timeslot';

export class ProviderAvailability {
  providerSlots: Map<number, TimeSlot[]> = new Map<number, TimeSlot[]>();

  constructor(json: IAvailabilityApiResponse) {
    const info = new AvailabilityResponse(json);
    const slots = info?.availableSlots ?? [];

    slots.forEach(({ start, end, participants, sessionType, locationId }) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (participants ?? []).forEach(({ providerId }) => {
        const list = this.providerSlots.get(providerId) ?? [];
        list.push({ start, end, participants, sessionType, locationId });
        this.providerSlots.set(providerId, list);
      });
    });

    // Sort each provider's slots by start date (earliest first)
    this.providerSlots.forEach((slotList, providerId) => {
      slotList.sort((a, b) => a.start.getTime() - b.start.getTime());
      this.providerSlots.set(providerId, slotList);
    });
  }
}

export type AvailabilityOptions = {
  participants: Participant[];
  required_duration: Duration;
  available_periods: AvailablePeriod[];
  response_format?: AvailabilityResponseFormat;
  // Omitted when the client stated no session format: v2 then applies its own hybrid default.
  session_type?: SchedulingServiceSessionType;
};

/** Options for the public (unauthenticated) availability read. */
export type PublicAvailabilityOptions = {
  slotDuration?: number;
  sessionType?: SessionType;
};

export type Participant = {
  members: ParticipantMember[];
  required: ParticipantsRequired;
};

export type ParticipantMember = {
  provider_id?: number;
  calendar_ids?: string;
  managed_availability?: boolean;
  provider_relationship?: ProviderRelationship;
};

export type CoreAvailabilityOptions = {
  participants: CoreParticipant[];
  availablePeriods: AvailablePeriod[];
  responseFormat: AvailabilityResponseFormat;
  sessionType: SchedulingServiceSessionType;
  // When set, scheduling-core bypasses session-duration resolution (e.g. client reschedule).
  requiredDuration?: Duration;
};

export type CoreParticipant = {
  members: CoreParticipantMember[];
  required: ParticipantsRequired;
};

export type CoreParticipantMember = {
  providerId?: string;
  calendarIds?: string;
  managedAvailability?: boolean;
  providerRelationship?: ProviderRelationship;
};

export type ProviderAvailabilityMember = {
  providerId: number;
  providerRelationship?: ProviderRelationship;
};

type Duration = {
  // must be greater than 0.
  minutes: number;
};
type AvailablePeriod = {
  start: string;
  end: string;
};

export enum ParticipantsRequired {
  ALL = 'all',
  ANY = 'any'
}

export enum AvailabilityResponseFormat {
  SLOTS = 'slots',
  PERIODS = 'periods',
  OVERLAPPING_SLOTS = 'overlapping_slots',
}

export enum ProviderRelationship {
  CARE_TEAM = 'care_team',
  MATCHED = 'matched',
  OTHER = 'other',
}
